import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { auth } from '../services/api';

interface User {
  id: string;
  email: string;
  full_name: string;
  role: string;
  venue_id?: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Helper function to check if token is expired
  const isTokenExpired = (token: string): boolean => {
    try {
      // Decode the token payload (base64)
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expiry = payload.exp * 1000; // Convert to milliseconds
      return Date.now() > expiry;
    } catch (error) {
      console.error('Error checking token expiry:', error);
      return true; // Treat as expired if we can't decode
    }
  };

  // Validate and restore session on mount
  useEffect(() => {
    const validateSession = async () => {
      const token = localStorage.getItem('auth_token');
      const storedUser = localStorage.getItem('user_data');
      
      if (token && storedUser) {
        // Check if token is expired
        if (isTokenExpired(token)) {
          console.log('Token expired, clearing session');
          localStorage.removeItem('auth_token');
          localStorage.removeItem('user_data');
          setLoading(false);
          return;
        }
        
        try {
          // Use stored user data
          const userData = JSON.parse(storedUser);
          setUser(userData);
          
          // Optional: Validate token with backend
          // This helps ensure the token is still valid on the server
          try {
            const response = await auth.validateToken();
            if (!response.data.valid) {
              throw new Error('Token invalid on server');
            }
          } catch (validationError) {
            console.warn('Token validation failed, continuing with stored data');
            // Continue with stored data even if validation fails
            // This prevents issues when the API is temporarily unavailable
          }
        } catch (error) {
          console.error('Invalid stored user data', error);
          localStorage.removeItem('auth_token');
          localStorage.removeItem('user_data');
        }
      } else if (token) {
        // Token exists but no user data - clear and require re-login
        localStorage.removeItem('auth_token');
      }
      setLoading(false);
    };
    
    validateSession();
  }, []);

  const login = async (email: string, password: string) => {
    const response = await auth.login(email, password);
    const { token, user } = response.data;
    localStorage.setItem('auth_token', token);
    localStorage.setItem('user_data', JSON.stringify(user));
    setUser(user);
  };

  const logout = () => {
    auth.logout();
    localStorage.removeItem('user_data');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};